import { statusColors } from '@parsina/framework/tokens';

/**
 * Appointment statuses as the DESK sees them — the framework's map, plus the two
 * patient-request states, and a bridge over the gap between them.
 *
 * `requested` and `rejected` belong in `@parsina/framework/tokens` and are there
 * in `packages/tokens/index.js`. But the framework reaches an app as a git tag
 * installed into `node_modules` (the installed one here is v0.13.2), so a token
 * added to the package is not a token the app HAS until someone cuts a release
 * and every client bumps its ref. In between, a chip reading the framework map
 * directly renders «نامشخص» over a perfectly well-defined status.
 *
 * So the two keys are defined here as a FALLBACK, applied only where the
 * installed framework has nothing. Spread order is what makes that true:
 * `statusColors` goes last, so the moment the tag lands its values win and these
 * become dead weight. Delete the `FALLBACK` block then; the rest of the module
 * stays.
 *
 * The patient app carries the same bridge, derived the same way
 * (`patient-app/parsina.patient.app/src/constants/appointmentStatus.js`).
 *
 * The LABELS are deliberately absent from this module. The clinic app is
 * trilingual and every label it renders comes from `useTranslation()`; a Persian
 * string baked into a constants file is precisely what the i18n ratchet exists to
 * keep out. What the framework map carries as `label` is ignored here — callers
 * ask `t('reception.requests.status.<key>')`.
 */
// Derived from swatches the installed framework ALREADY has, never spelled as
// hex. `packages/tokens` defines the two new statuses with exactly these
// colours — `requested` is `waiting`'s amber, `rejected` is `no_show`'s grey —
// so reusing them is not an approximation, it is the same values reached
// without copying them. That keeps this file inside the no-raw-hex rule (it
// needs no exemption) and makes a drift impossible: if the palette moves, the
// bridge moves with it.
const FALLBACK = {
  requested: { ...statusColors.waiting },
  rejected: { ...statusColors.no_show },
};

export const APPOINTMENT_STATUS = { ...FALLBACK, ...statusColors };

function normalize(status) {
  return String(status || '').toLowerCase().replace(/-/g, '_');
}

/**
 * Colours for an appointment status, or null for one we do not know.
 *
 * Null rather than a default swatch: a confident colour on a status nobody
 * planned for is a lie the reader cannot see through, and the caller can render
 * the raw word instead — the same rule every other status map in this app keeps.
 */
export function appointmentStatusColors(status) {
  const key = normalize(status);
  return APPOINTMENT_STATUS[key] || null;
}

/**
 * The reason codes «رد» offers the desk, in the order the dialog lists them.
 *
 * `expired` is deliberately absent: it is written by the scheduling service's own
 * lazy sweep when nobody answered in time, and offering it here would let a desk
 * record a decision that nothing made.
 */
export const REJECTION_REASONS = [
  'no_capacity', 'practitioner_unavailable', 'patient_not_eligible', 'other',
];

/**
 * The statuses that RELEASE a provider's hour, mirroring the scheduling
 * service's own `_holds_slot` predicate
 * (`postgres_repository.py`, PATIENT_CLINIC_CARE_CONTEXT §5.2.1).
 *
 * `rejected` belongs here beside `cancelled`, and that is the point of the pair:
 * the clinic answering «رد» to a patient's request frees the hour exactly as a
 * cancellation does — the desk said no, so the slot goes back on the market and
 * the next caller can have it. The booking grid used to drop only `cancelled`,
 * so a refused request went on hiding its hour from every later booking although
 * the service would have accepted one there: the desk refused a patient AND lost
 * the slot.
 *
 * `no_show` and `completed` are deliberately NOT here. The service holds their
 * hour — the visit was booked and either happened or was missed — and a grid that
 * offered a slot the server then refuses is worse than one that shows it taken.
 * This list tracks the SERVER's rule, not what feels free.
 */
export const SLOT_FREEING_STATUSES = ['cancelled', 'rejected'];

/**
 * Does this appointment still occupy its provider's hour?
 *
 * Takes the row rather than the status alone because one status is not enough to
 * answer: a `requested` row holds its slot (that is what a request IS) but only
 * until `requestExpiresAt`, and an expired request stops holding it the instant it
 * lapses rather than when some sweep gets around to it — the same rule the service
 * applies, so the grid and the booking it leads to can never disagree.
 */
export function holdsSlot(appointment, nowMs = Date.now()) {
  const status = normalize(appointment?.status);
  if (SLOT_FREEING_STATUSES.includes(status)) return false;
  if (status === 'requested') {
    const expires = Number(appointment?.requestExpiresAt);
    if (Number.isFinite(expires) && expires > 0 && expires <= nowMs) return false;
  }
  return true;
}
